import { cn } from '~/utils/cn';
import {
  QUOTE_PRODUCT_COLOR,
  QUOTE_PRODUCT_SYMBOL,
  formatQuotePrice,
  ufName,
  unitSuffix,
} from '~/utils/quotes';
import type { QuoteItem, QuoteProduct } from '~/store/quotes/interfaces';
import type { propsApiMarketIndexRate } from '~/services/http/interfaces';

import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';
import QuoteTrend from '../QuoteTrend/view';

interface PropsQuotesTicker {
  items: QuoteItem[];
  marketIndexes?: propsApiMarketIndexRate[];
  uf: string | null;
  onClickItem?: (product: QuoteProduct) => void;
  onClickHeader?: () => void;
  className?: string;
}

function Chip({
  item,
  onClick,
}: {
  item: QuoteItem;
  onClick?: (product: QuoteProduct) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick?.(item.product)}
      className="flex items-center px-smaller shrink-0 cursor-pointer"
    >
      <span
        className="w-[7px] h-[7px] rounded-full mr-quark shrink-0"
        style={{ backgroundColor: QUOTE_PRODUCT_COLOR[item.product] }}
      />
      <Text className="text-captionLG color-intense-04 mr-quark">
        {QUOTE_PRODUCT_SYMBOL[item.product]}
      </Text>
      <Text className="text-bodyMD color-intense-01 mr-quark">
        {`${formatQuotePrice(item.price, item.unit)}${unitSuffix(item.unit)}`}
      </Text>
      <QuoteTrend pct={item.change?.pct ?? null} compact />
    </button>
  );
}

export default function QuotesTicker({
  items,
  marketIndexes = [],
  uf,
  onClickItem,
  onClickHeader,
  className,
}: PropsQuotesTicker) {
  const tickerItems = [
    ...items,
    ...marketIndexes.filter((rate) => rate.indexer === 'cdi' || rate.indexer === 'selic'),
  ];
  const hasItems = tickerItems.length > 0;

  const RateChip = ({ rate }: { rate: propsApiMarketIndexRate }) => (
    <div className="flex items-center px-smaller shrink-0">
      <span className="w-[7px] h-[7px] rounded-full mr-quark shrink-0 bg-warning-03" />
      <Text className="text-captionLG color-intense-04 mr-quark uppercase">
        {rate.indexer}
      </Text>
      <Text className="text-bodyMD color-intense-01 mr-quark">
        {`${(rate.value * 100).toFixed(2)}% a.a.`}
      </Text>
    </div>
  );

  const TickerContent = () => (
    <>
      {items.map((it) => (
        <Chip key={it.product} item={it} onClick={onClickItem} />
      ))}
      {marketIndexes
        .filter((rate) => rate.indexer === 'cdi' || rate.indexer === 'selic')
        .map((rate) => <RateChip key={rate.indexer} rate={rate} />)}
    </>
  );

  return (
    <div className={cn('rounded-medium bg-primary-01 overflow-hidden shadow-level1', className)}>
      <button
        type="button"
        onClick={onClickHeader}
        className="flex items-center justify-between w-full px-small pt-smaller pb-quark"
      >
        <div className="flex items-center gap-quark">
          <span className="w-[6px] h-[6px] rounded-full bg-success-03" />
          <Text className="text-captionMD color-intense-04 uppercase">
            Mercado agora
          </Text>
        </div>
        <div className="flex items-center gap-quark">
          <Text className="text-captionMD color-intense-03">{ufName(uf)}</Text>
          <Icon name="icon-chevron-right" size={14} color="#B0B0B0" rounded={false} />
        </div>
      </button>

      <div className="overflow-hidden pb-smaller">
        {hasItems ? (
          <div className="flex items-center w-max animate-marquee">
            <TickerContent />
            <TickerContent />
          </div>
        ) : (
          <div className="flex px-small gap-small">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[16px] w-[96px] rounded-full bg-intense-01/15" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
